import { useEffect, useState } from "react";
import styles from "./Toaster.module.css";

export type ToastVariant = "success" | "error" | "info";

export interface ToastInput {
  message: string;
  variant?: ToastVariant;
  /** Auto-dismiss delay in ms. Set to 0 to make the toast sticky. */
  duration?: number;
}

interface Toast extends ToastInput {
  id: number;
  variant: ToastVariant;
  duration: number;
}

type Listener = (queue: Toast[]) => void;

const DEFAULT_DURATION = 3500;
const MAX_TOASTS = 3;

let queue: Toast[] = [];
let nextId = 1;
const listeners = new Set<Listener>();

function emit() {
  for (const l of listeners) l(queue.slice());
}

/**
 * Imperative API. Safe to call from anywhere (mutation onSuccess, plain
 * callbacks, etc.). The mounted <Toaster /> subscribes and renders.
 */
export function toast(input: ToastInput): number {
  const id = nextId++;
  const next: Toast = {
    id,
    message: input.message,
    variant: input.variant ?? "info",
    duration: input.duration ?? DEFAULT_DURATION,
  };
  queue = [...queue, next].slice(-MAX_TOASTS);
  emit();
  return id;
}

export function dismissToast(id: number) {
  const before = queue.length;
  queue = queue.filter((t) => t.id !== id);
  if (queue.length !== before) emit();
}

export const toastSuccess = (message: string, duration?: number) =>
  toast({ message, variant: "success", duration });
export const toastError = (message: string, duration?: number) =>
  toast({ message, variant: "error", duration });
export const toastInfo = (message: string, duration?: number) =>
  toast({ message, variant: "info", duration });

/**
 * Renders the toast region. Mount exactly once at the App root.
 *
 * Region semantics: `aria-live="polite"` so screen readers announce new
 * toasts without preempting current speech. Bottom-right on desktop,
 * stretched along the bottom on phones.
 */
export function Toaster() {
  const [items, setItems] = useState<Toast[]>([]);

  useEffect(() => {
    const sync: Listener = (next) => setItems(next);
    listeners.add(sync);
    sync(queue);
    return () => {
      listeners.delete(sync);
    };
  }, []);

  useEffect(() => {
    const timers = items
      .filter((t) => t.duration > 0)
      .map((t) =>
        window.setTimeout(() => dismissToast(t.id), t.duration),
      );
    return () => {
      for (const id of timers) window.clearTimeout(id);
    };
  }, [items]);

  if (items.length === 0) return null;

  return (
    <div className={styles.region} role="region" aria-live="polite" aria-label="Notifications">
      {items.map((t) => {
        const variantClass =
          t.variant === "success"
            ? styles.success
            : t.variant === "error"
            ? styles.error
            : styles.info;
        return (
          <div key={t.id} className={`${styles.toast} ${variantClass}`}>
            <span className={styles.message}>{t.message}</span>
            <button
              type="button"
              className={styles.dismiss}
              aria-label="Dismiss notification"
              onClick={() => dismissToast(t.id)}
            >
              ×
            </button>
          </div>
        );
      })}
    </div>
  );
}

// Test-only seam. Not exported through the barrel.
export function __resetToasterForTests() {
  queue = [];
  nextId = 1;
  emit();
}
